import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { Split, Users, MessagesSquare, ShieldCheck, KeyRound, ArrowRight, Tag, Gift, Percent, Package, Mail, Send, Loader2, CheckCircle2 } from "lucide-react";

const DEAL_TYPES = [
  {
    icon: Gift,
    badge: "Free",
    badgeCls: "bg-green-600",
    accent: "text-green-700",
    ring: "border-green-200",
    tint: "bg-green-50",
    title: "Buy One, Get One Free",
    text: "Two shoppers split the cost of one item and each walk away with one. Pay half, waste nothing.",
    example: "$32 item → you pay $16",
  },
  {
    icon: Percent,
    badge: "50% off",
    badgeCls: "bg-orange-500",
    accent: "text-orange-600",
    ring: "border-orange-200",
    tint: "bg-orange-50",
    title: "Buy One, Get One at a Discount",
    text: "The second item is discounted. Team up, share the savings on the reduced item, and both come out ahead.",
    example: "Save up to 50% on the 2nd item",
  },
  {
    icon: Package,
    badge: "Multi-pack",
    badgeCls: "bg-blue-600",
    accent: "text-blue-700",
    ring: "border-blue-200",
    tint: "bg-blue-50",
    title: "Multi-Pack Split",
    text: "Perfect for Costco-style bulk buys. The owner sets each partner's share and can split a big pack several ways.",
    example: "24-pack → your share, your call",
  },
];

const STEPS = [
  { icon: Tag, title: "Spot a curated deal", text: "We hand-pick BOGO offers from flyers & retailer apps. Browse the board and claim one." },
  { icon: Users, title: "Get matched", text: "You take one half of the deal, a second shopper takes the other. No more wasted freebies." },
  { icon: MessagesSquare, title: "Chat & agree a fee", text: "Message in real time and settle on a fair price for the second item." },
  { icon: KeyRound, title: "Exchange the code", text: "The buyer shares the redemption code. Once confirmed, funds are released to them." },
];

const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      return toast.error("Please fill in every field.");
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Thanks! We'll be in touch soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send your message. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="border-t border-black/10 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-background px-3 py-1 text-xs font-semibold text-primary">
            <Mail className="h-3.5 w-3.5" /> Get in touch
          </span>
          <h2 className="mt-4 font-heading text-3xl font-700 tracking-tight" style={{ fontWeight: 700 }}>
            Questions, ideas, or a deal to suggest?
          </h2>
          <p className="mt-3 text-muted-foreground max-w-md leading-relaxed">
            Drop us a line and the BogoShare team will get back to you. Whether it's a partnership,
            a bug, or a BOGO you'd love to see on the board — we're listening.
          </p>
        </div>

        {sent ? (
          <div data-testid="contact-success" className="rounded-xl border border-green-200 bg-green-50 p-8 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" />
            <p className="mt-3 font-heading text-lg" style={{ fontWeight: 700 }}>Message sent!</p>
            <p className="mt-1 text-sm text-muted-foreground">Thanks for reaching out — we'll reply to your email shortly.</p>
            <Button variant="outline" data-testid="contact-send-another" onClick={() => setSent(false)} className="mt-4 rounded-full border-black/20">
              Send another
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form" className="rounded-xl border border-black/10 bg-background p-6 space-y-4">
            <div>
              <Label htmlFor="contact-name">Name</Label>
              <Input id="contact-name" data-testid="contact-name-input" value={form.name} onChange={set("name")}
                placeholder="Jane Doe" className="mt-1" required />
            </div>
            <div>
              <Label htmlFor="contact-email">Email</Label>
              <Input id="contact-email" type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")}
                placeholder="you@example.com" className="mt-1" required />
            </div>
            <div>
              <Label htmlFor="contact-message">Message</Label>
              <Textarea id="contact-message" data-testid="contact-message-input" value={form.message} onChange={set("message")}
                placeholder="How can we help?" rows={4} className="mt-1" required />
            </div>
            <Button type="submit" data-testid="contact-submit-btn" disabled={busy}
              className="w-full h-11 rounded-full hard-shadow-sm hover:-translate-y-0.5 transition-transform duration-200">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Send message
            </Button>
          </form>
        )}
      </div>
    </section>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background grain-bg">
      <Navbar />

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-16 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1 text-xs font-semibold text-primary">
              <Split className="h-3.5 w-3.5" /> Buy One, Get One — Split with a stranger
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-900 tracking-tight leading-[1.05]" style={{ fontWeight: 900 }}>
              Never waste the <span className="text-primary">free half</span> of a deal again.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              BogoShare pairs shoppers on the same deal — Buy-One-Get-One, discounted seconds, or bulk multi-packs.
              Take what you need, hand off the rest, agree a fair fee, and settle safely through a code exchange.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                size="lg"
                data-testid="hero-cta-primary"
                className="rounded-full h-12 px-7 hard-shadow-sm hover:-translate-y-1 transition-transform duration-200"
                onClick={() => navigate("/browse")}
              >
                Browse deals <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                data-testid="hero-cta-secondary"
                className="rounded-full h-12 px-7 border-black/20 hover:-translate-y-1 transition-transform duration-200"
                onClick={() => navigate("/login")}
              >
                I have an account
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-accent" />
              Funds released only after both sides confirm the code.
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-xl border border-black/10 bg-white hard-shadow">
              <img
                src="https://images.pexels.com/photos/5650023/pexels-photo-5650023.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Shopping bags"
                className="w-full h-[420px] object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 rounded-lg border border-black/10 bg-white p-4 hard-shadow-sm">
              <p className="text-xs text-muted-foreground">Your portion</p>
              <p className="font-heading text-2xl font-700" style={{ fontWeight: 700 }}>
                <span className="line-through text-muted-foreground/60 text-lg mr-2">$32</span>$16
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Deal types */}
      <section className="border-t border-black/10 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
          <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-background px-3 py-1 text-xs font-semibold text-primary">
            <Tag className="h-3.5 w-3.5" /> Three ways to split
          </span>
          <h2 className="mt-4 font-heading text-3xl font-700 tracking-tight" style={{ fontWeight: 700 }}>
            Deals for every kind of shop
          </h2>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            From free second items to Costco-style bulk packs — pick the split that fits and pay only for your share.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {DEAL_TYPES.map((d, i) => (
              <motion.div
                key={d.title}
                data-testid={`deal-type-card-${i}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className={`rounded-xl border ${d.ring} ${d.tint} p-6 hover:-translate-y-1 transition-transform duration-200`}
              >
                <div className="flex items-center justify-between">
                  <div className={`flex h-11 w-11 items-center justify-center rounded-lg bg-white ${d.accent} border border-black/5`}>
                    <d.icon className="h-5 w-5" />
                  </div>
                  <span className={`rounded-full ${d.badgeCls} text-white text-xs font-semibold px-2.5 py-1`}>{d.badge}</span>
                </div>
                <h3 className="mt-4 font-heading text-lg font-700" style={{ fontWeight: 700 }}>{d.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{d.text}</p>
                <p className={`mt-4 text-sm font-semibold ${d.accent}`}>{d.example}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8">
            <Button
              data-testid="deal-types-cta"
              className="rounded-full h-11 px-6 hard-shadow-sm hover:-translate-y-1 transition-transform duration-200"
              onClick={() => navigate("/browse")}
            >
              Browse all deals <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-black/10 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
          <h2 className="font-heading text-3xl font-700 tracking-tight" style={{ fontWeight: 700 }}>
            How the split works
          </h2>
          <p className="mt-2 text-muted-foreground max-w-2xl">Four steps from browsing to a settled, verified deal.</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-lg border border-black/10 bg-background p-6 hover:-translate-y-1 transition-transform duration-200"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <s.icon className="h-5 w-5" />
                </div>
                <p className="mt-4 text-xs font-mono-code text-muted-foreground">STEP {i + 1}</p>
                <h3 className="mt-1 font-heading text-lg font-600" style={{ fontWeight: 600 }}>{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ContactSection />

      <footer className="border-t border-black/10 py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-sm text-muted-foreground">
          <span className="font-heading font-700" style={{ fontWeight: 700 }}>BogoShare</span>
          <span>Split smarter. Waste nothing.</span>
          <div className="flex items-center gap-4">
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-foreground">Terms of Service</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-foreground">Privacy Policy</Link>
          </div>
        </div>
        <p className="mx-auto max-w-7xl px-4 sm:px-6 mt-4 text-xs text-muted-foreground/80 leading-relaxed">
          BogoShare is not affiliated with, endorsed by, or sponsored by any retailer listed. All retailer names
          and trademarks belong to their respective owners and are used for factual identification only.
        </p>
      </footer>
    </div>
  );
}
